import { Add01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { SURVEY_TEMPLATES, SurveyTemplate } from "../surveyTemplates";
import { NewSurvey } from "@/types/surveys";

interface CreateSurveyDialogProps {
  open: boolean;
  onClose: () => void;
  isLoading?: boolean;
  onSelectTemplate: (surveyDraft: Partial<NewSurvey>) => void;
}

const CreateSurveyDialog = ({
  open,
  onClose,
  isLoading,
  onSelectTemplate,
}: CreateSurveyDialogProps) => {
  const handleCreateBlank = () => {
    onSelectTemplate({
      title: "Nueva Encuesta",
      description: "",
      trigger: "manual",
      isPublished: false,
      questions: [
        {
          id: crypto.randomUUID(),
          title: "¿Cómo calificarías tu experiencia?",
          type: "rating",
          required: true,
          options: [],
        },
      ],
    });
  };

  const handleSelect = (template: SurveyTemplate) => {
    // Regenerar IDs únicos para preguntas y opciones
    const questions = (template.survey.questions || []).map((q) => ({
      ...q,
      id: crypto.randomUUID(),
      options: (q.options || []).map((opt) => ({
        ...opt,
        id: crypto.randomUUID(),
      })),
    }));

    onSelectTemplate({
      ...template.survey,
      questions,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crear Nueva Encuesta</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} pt={1}>
          <Typography variant="body2" color="text.secondary">
            Elige una plantilla diseñada para restaurantes o comienza con una encuesta en blanco.
          </Typography>

          <Stack spacing={1.5}>
            {SURVEY_TEMPLATES.map((tpl) => (
              <Paper
                key={tpl.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  cursor: isLoading ? "default" : "pointer",
                  transition: "border-color 0.2s, background-color 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
                onClick={() => !isLoading && handleSelect(tpl)}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      {tpl.name}
                    </Typography>
                    <Chip label={tpl.badge} size="small" color="primary" variant="outlined" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {tpl.description}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {tpl.survey.questions?.length || 0} preguntas incluidas
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Divider>o</Divider>

          <Box>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<HugeiconsIcon icon={Add01Icon} />}
              disabled={isLoading}
              onClick={handleCreateBlank}
            >
              Comenzar desde cero (En blanco)
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSurveyDialog;
